import {
  AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn,
  Validators
} from '@angular/forms';
import { states } from './states';
import * as _ from 'lodash';
import * as moment from 'moment';

export const emailMatcher: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const emailAddress = control.get('emailAddress');
  const confirmEmail = control.get('confirmEmail');
  if (!emailAddress || !confirmEmail) {
    return null;
  } else if (emailAddress.value === confirmEmail.value) {
    return null;
  } else {
    return { nomatch: true };
  }
};

export const mustHaveProduct: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const result = control.get('eligibleProducts')!.value.filter(product => product.isEligible !== false);

  return result.length > 0 ? null : { mustHaveProduct: true };
};

export const mustHaveUniqueIncentives: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const chosenIds = control.get('incentives')!.value.map(incentive => incentive.incentiveId).filter(id => id !== '');
  const isValid = chosenIds.length === _.uniq(chosenIds).length;
  return isValid ? null : { mustHaveUniqueIncentives: true };
};

export const expirationDateInThePast: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const expirationDate = control.value;
  const [expMonth, expYear] = expirationDate.split('-').map((d: string) => parseInt(d, 10));
  const now = new Date(),
    currentMonth = now.getMonth() + 1,
    currentYear = now.getFullYear();

  const isInPast = expYear < currentYear ||
    (expYear === currentYear && expMonth < currentMonth);

  if (!expirationDate || !isInPast) {
    return null;
  } else {
    return { expirationDateInThePast: true };
  }
};

export const graduationYearInThePast: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const graduationYear = control.value;
  const now = new Date(),
    currentYear = now.getFullYear();

  const isInPast = graduationYear < currentYear;

  if (!graduationYear || !isInPast) {
    return null;
  } else {
    return { graduationYearInThePast: true };
  }
};

export const enrollmentVsGraduationYearCheck: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const startYearHandle = group.get('startYear');
  const gradYearHandle = group.get('graduationYear');
  if (!startYearHandle || !gradYearHandle) {
    return null;
  } else {
    const startYear = parseInt(startYearHandle.value, 10);
    const gradYear = parseInt(gradYearHandle.value, 10);
    if (gradYear < startYear) {
      return { graduationYearBeforeEnrollmentYear: true };
    } else if (gradYear > startYear + 10) {
      return { graduationYearTooFarInTheFuture: true };
    } else {
      return null;
    }
  }
};

export const endDateBeforeStartDate: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const startHandle = group.get('startDate')!;
  const endHandle = group.get('endDate')!;

  const startDate = moment(reverseDate(startHandle.value));
  const endDate = moment(reverseDate(endHandle.value));
  return endDate < startDate
    ? { endDateBeforeStartDate: true }
    : null;
};

export const isStateValidator: ValidatorFn = ((state: AbstractControl): ValidationErrors | null => {
  const abbreviation = state.value;
  return _.includes(states, abbreviation) ? null : {'invalid state': abbreviation};
});

export function showAllErrors(group: FormGroup | FormArray) {
  group.markAsTouched();
  for (const i in group.controls) {
    const control = (group.controls as any)[i];
    if (control instanceof FormControl) {
      control.markAsTouched();
    } else {
      showAllErrors(control);
    }
  }
}

export const fieldMasks = {
  cvv: [/\d/, /\d/, /\d/, /\d/],
  date: [ /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  dateOfBirth: [ /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  dateOfBirthISO: [ /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/],
  expirationDate: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  telephoneNumber: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  year: [/\d/, /\d/, /\d/, /\d/],
  zipCode: [/\d/, /\d/, /\d/, /\d/, /\d/],
};

export const fieldPatterns = {
  cardNumber: /^[0-9]{13,19}$/,
  creditCardName: /^\w{1,30}(\s.{0,30})?$/,
  cvv: /^(?:\d{3}|\d{4})$/,
  expirationDate: /^(?:[0][1-9]|[1][0-2])-20\d{2}$/,
  date: /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/,
  dateOfBirth: /(0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])[- \/.](19|20)\d\d/,
  dateOfBirthISO: /(19|20)\d\d[- \/.](0[1-9]|1[012])[- \/.](0[1-9]|[12][0-9]|3[01])/,
  isoDate: /(19|20)\d\d[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])/,
  // telephoneNumber: /^\+?(\d{1,4}?[-.\s]?)?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
  telephoneNumber: /^[\d\-\+ \.\(\)]+$/, // allows numbers, dash, plus, spaces, periods, and parentheses.
  smartSearch: /(.+) (\w+)/,
  year: /(?:19|20)\d\d/,
  zipCode: /\d{5}/,
  emailAddress: /^[\w._-]+@[a-zA-Z_0-9-]+?\.[a-zA-Z.]{1,}[^.]$/,
  effortCodes: /^[a-zA-Z0-9 ,]+[\r\n|\r|\n]*$/,
  phoneNumber: /^[0-9]{10}$/,
  molecularFormula: /^[\da-zA-Z\.\s,\-\(\)\/;•]+$/
};

export function reverseDate(dob: string) {
  const [mm, dd, yyyy] = dob.split('-');
  return `${yyyy}-${mm}-${dd}`;
}

export function reverseISODate(dob: string) {
  const [yyyy, mm, dd] = dob.split('-');
  return `${mm}-${dd}-${yyyy}`;
}

export function mapToOptions<T> (options: T[]) {
  return options.map((option: T) => {
    return {value: String(option), key: String(option)};
  });
}

export const parseEffortCodes = (rawCodes: string): string[] => {
  if (rawCodes) {
    return rawCodes
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);
  }

  return [];
}

export type DateRange = {
  start: moment.Moment;
  end: moment.Moment;
}

export const doDateRangesOverlap = (range1: DateRange, range2: DateRange): boolean => {
  return range1.start.isBetween(range2.start, range2.end, "day", "[]")
    || range1.end.isBetween(range2.start, range2.end, "day", "[]")
    || range2.start.isBetween(range1.start, range1.end, "day", "[]");
}
